import {
  getAuthTypeFromConfig,
  getOpenApiTenantNameFromConfig,
  hasUnresolvedTenant,
  isAuthenticatedDatasourceConfig,
  isAuthenticatedDatasourceConfigWithDynamicAuthType,
  isDatasourceAuthState,
  isPkceEnabled,
  resolveAuthConfigTenantUrls,
} from "@superblocksteam/shared";
import type {
  AuthConfig,
  AuthType,
  DatasourceAuthState,
  DatasourceOneTimeState,
  SupersetIntegrationDto,
} from "@superblocksteam/shared";

import {
  getConfigFromIntegrationWithProfileId,
  getConfigIdFromIntegrationWithProfileId,
} from "store/slices/datasources/utils";

export type ResolvedDatasourceConfig = {
  authConfig: AuthConfig;
  authType: AuthType;
  pluginId: string;
  integrationId: string;
  configurationId: string;
  // orchestrator can fetch authConfig and authType from the server when
  // exchanging code; `configSource` records whether the values above
  // came from the popup's own redux/datasource fetch ("server") or
  // from the embed shell via `state` ("client").
  configSource: "server" | "client";
};

export function getPkceCallbackConfigurationError(args: {
  externalUser: boolean;
  refreshTokenFromServer: boolean | undefined;
  usePkce: unknown;
}): string | undefined {
  if (!isPkceEnabled(args.usePkce)) {
    return undefined;
  }
  if (args.externalUser) {
    return "PKCE is not supported for embedded external-user callbacks.";
  }
  if (args.refreshTokenFromServer) {
    return "Failed to exchange an authorization code for an access token. PKCE requires an agent-managed public OAuth client.";
  }
  return undefined;
}

/**
 * A per-tenant integration whose tenant was missing or invalid still carries the placeholder in
 * its token URL. The exchange would post the client secret at an unresolvable host and fail on
 * the server, so name the misconfiguration here instead.
 */
export function getTenantCallbackConfigurationError(
  authConfig: Partial<AuthConfig> | undefined,
): string | undefined {
  if (hasUnresolvedTenant(authConfig?.tokenUrl)) {
    return "The integration's subdomain is missing or invalid, so its OAuth 2.0 token URL cannot be resolved.";
  }
  return undefined;
}

export function shouldRequestOAuthContextFromOpener(args: {
  authState: DatasourceAuthState | DatasourceOneTimeState;
  handoffMode: "localStorage" | "postMessage";
  resolvedAuthConfig: Partial<AuthConfig> | undefined;
}): boolean {
  const { authState, handoffMode, resolvedAuthConfig } = args;

  if (authState.externalUser) {
    return false;
  }

  if (isDatasourceAuthState(authState) && !authState.authConfig.clientSecret) {
    return true;
  }

  return (
    handoffMode === "postMessage" && isPkceEnabled(resolvedAuthConfig?.usePkce)
  );
}

/**
 * Resolve the integration configuration the OAuth callback should
 * exchange the access code against.
 *
 * Three independent paths, tried in order:
 *
 *  1. Full `DatasourceAuthState` in `authState` — only the legacy
 *     in-app OAuth modal produces this shape, and it carries every
 *     field the callback needs verbatim. Used when present.
 *  2. `authState.configurationId` set by the deployed-embed shell
 *     plus a fetched `datasource` — pick the configuration with the
 *     matching id directly. This is the embed-popup path: the popup
 *     runs in the customer's logged-out browser context, so it has
 *     no Redux `organization.profiles` to drive the profile-based
 *     lookup, but the shell knows exactly which configuration it
 *     resolved against and forwards its id in `state`.
 *  3. Redux `profileId` + the fetched `datasource` — the historical
 *     in-app path that does a `profileIds.includes(profileId)`
 *     lookup with an `isDefault` fallback.
 */
export function resolveOAuthCallbackConfig(args: {
  authConfigFromOpener?: Partial<AuthConfig>;
  datasource: SupersetIntegrationDto | undefined;
  profileId: string;
  authState: DatasourceAuthState | DatasourceOneTimeState | undefined;
}): ResolvedDatasourceConfig | undefined {
  const { authConfigFromOpener, datasource, profileId, authState } = args;

  if (
    authState &&
    isDatasourceAuthState(authState) &&
    (authState.authConfig.clientSecret || authConfigFromOpener)
  ) {
    return {
      authConfig: {
        ...authState.authConfig,
        ...authConfigFromOpener,
      } as AuthConfig,
      authType: authState.authType as AuthType,
      pluginId: authState.pluginId,
      integrationId: authState.integrationId,
      configurationId: authState.configurationId,
      configSource: "client",
    };
  }

  if (datasource && authState?.configurationId) {
    const matched = datasource.configurations?.find(
      (c) => c.id === authState.configurationId,
    );
    if (
      matched?.configuration &&
      (isAuthenticatedDatasourceConfig(matched.configuration) ||
        isAuthenticatedDatasourceConfigWithDynamicAuthType(
          matched.configuration,
        ))
    ) {
      return {
        // A stored config for a per-tenant provider holds its OAuth URLs with the tenant
        // placeholder; the exchange needs them pointed at the customer's own host.
        authConfig: resolveAuthConfigTenantUrls(
          matched.configuration.authConfig as AuthConfig,
          getOpenApiTenantNameFromConfig(matched.configuration),
        ),
        authType: getAuthTypeFromConfig(matched.configuration) as AuthType,
        pluginId: datasource.pluginId,
        integrationId: datasource.id,
        configurationId: matched.id,
        configSource: "server",
      };
    }
  }

  const existingConfig = getConfigFromIntegrationWithProfileId(
    datasource,
    profileId,
  );
  if (
    datasource &&
    existingConfig &&
    (isAuthenticatedDatasourceConfigWithDynamicAuthType(existingConfig) ||
      isAuthenticatedDatasourceConfig(existingConfig))
  ) {
    return {
      authConfig: resolveAuthConfigTenantUrls(
        existingConfig.authConfig as AuthConfig,
        getOpenApiTenantNameFromConfig(existingConfig),
      ),
      authType: getAuthTypeFromConfig(existingConfig) as AuthType,
      pluginId: datasource.pluginId,
      integrationId: datasource.id,
      configurationId: getConfigIdFromIntegrationWithProfileId(
        datasource,
        profileId,
      ) as string,
      configSource: "server",
    };
  }
  return undefined;
}
